// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\PartDetail.jsx
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Context } from "../store/appContext";
import getApiBaseUrl from "../utils/api";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/catalog.css";

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
const formatNumber = (value) => numberFormatter.format(Number(value || 0));
const formatInteger = (value) => integerFormatter.format(Number(value || 0));
const formatDate = (value) => value || "-";
const PURCHASE_HISTORY_PAGE_SIZE = 10;
const DESCRIPTION_TRUNCATE_LENGTH = 64;
const API_URL = getApiBaseUrl();
const PART_DOCUMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;
const PART_DOCUMENT_ACCEPT = ".pdf,.xlsx,.xls,.docx,.doc,.jpg,.jpeg,.png";
const PART_DOCUMENT_TYPES = [
  ["MANUAL", "Manual"],
  ["PLANO", "Plano"],
  ["FICHA_TECNICA", "Ficha técnica"],
  ["CERTIFICADO", "Certificado"],
  ["FOTO", "Foto"],
  ["PRESUPUESTO", "Presupuesto"],
  ["OTRO", "Otro"],
];
const PART_DOCUMENT_TYPE_LABELS = Object.fromEntries(PART_DOCUMENT_TYPES);

const formatFileSize = (value) => {
  const size = Number(value || 0);
  if (!size) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${formatNumber(size / 1024)} KB`;
  return `${formatNumber(size / (1024 * 1024))} MB`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPartDocumentErrorMessage = (result, fallbackMessage) => {
  const error = result?.error;
  if (typeof error === "string" && error.trim()) return error;
  if (error && typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }
  return fallbackMessage;
};

const getMonthKey = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return "-";

  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);

  if (Number.isNaN(date.getTime())) return monthKey;

  return date.toLocaleDateString("es-ES", {
    month: "short",
    year: "numeric",
  });
};

const truncateText = (value, maxLength = DESCRIPTION_TRUNCATE_LENGTH) => {
  const text = String(value || "").trim();

  if (!text) return "-";
  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength - 3)}...`;
};

const getSupplierDetailPath = (supplierId) =>
  supplierId ? `/suppliers/${encodeURIComponent(supplierId)}` : "#";

const getSortIndicator = (sortConfig, key) => {
  if (sortConfig.key !== key) {
    return "";
  }

  return sortConfig.direction === "asc" ? " ▲" : " ▼";
};

const getPartMachineItems = (payload) => {
  if (Array.isArray(payload)) return payload;

  for (const key of [
    "items",
    "results",
    "relations",
    "machines",
    "machine_spare_parts",
  ]) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  return [];
};

const getCanonicalMaterialRecordId = (detail) => {
  const candidates = [
    detail?.material_record_id,
    detail?.internal_material_id,
    detail?.material_id_internal,
    detail?.material?.material_record_id,
    detail?.material?.internal_material_id,
    detail?.material?.id,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isInteger(parsed) && parsed > 0) return parsed;
  }

  return null;
};

const getMachineRelationMachine = (relation) =>
  relation?.machine ||
  relation?.machine_summary ||
  relation?.equipment ||
  {};

const getMachineRelationUnit = (relation, machine) =>
  machine?.factory_unit ||
  machine?.unit ||
  relation?.factory_unit ||
  relation?.unit ||
  {};

const getMachineRelationLabel = (options, value) =>
  options.find(([code]) => code === value)?.[1] ||
  value ||
  "Sin dato";

const PART_MACHINE_RELATIONSHIP_TYPES = [
  ["COMPONENTE", "Componente"],
  ["CONSUMIBLE", "Consumible"],
  ["DESGASTE", "Desgaste"],
  ["AUXILIAR", "Auxiliar"],
];

const PART_MACHINE_CRITICALITIES = [
  ["SIN_CLASIFICAR", "Sin clasificar"],
  ["BAJA", "Baja"],
  ["MEDIA", "Media"],
  ["ALTA", "Alta"],
  ["CRITICA", "Crítica"],
];

const PartDetail = () => {
  const { material_id: materialId } = useParams();
  const navigate = useNavigate();
  const { store, actions } = useContext(Context);
  const [purchaseHistoryPage, setPurchaseHistoryPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [partDocuments, setPartDocuments] = useState([]);
  const [associatedMachines, setAssociatedMachines] = useState([]);
  const [associatedMachinesLoading, setAssociatedMachinesLoading] = useState(false);
  const [associatedMachinesError, setAssociatedMachinesError] = useState("");
  const [associatedMachineMode, setAssociatedMachineMode] = useState("true");
  const [partDocumentsLoading, setPartDocumentsLoading] = useState(false);
  const [partDocumentsError, setPartDocumentsError] = useState("");
  const [partDocumentUploadOpen, setPartDocumentUploadOpen] = useState(false);
  const [partDocumentFile, setPartDocumentFile] = useState(null);
  const [partDocumentType, setPartDocumentType] = useState("MANUAL");
  const [partDocumentDescription, setPartDocumentDescription] = useState("");
  const [partDocumentSubmitting, setPartDocumentSubmitting] = useState(false);
  const [partDocumentUploadError, setPartDocumentUploadError] = useState("");
  const [partDocumentPreview, setPartDocumentPreview] = useState(null);
  const [partDocumentPreviewingId, setPartDocumentPreviewingId] = useState(null);
  const [partDocumentPreviewError, setPartDocumentPreviewError] = useState("");
  const [partDocumentDownloadingId, setPartDocumentDownloadingId] = useState(null);
  const [partDocumentDownloadError, setPartDocumentDownloadError] = useState("");
  const [partDocumentDeleteTarget, setPartDocumentDeleteTarget] = useState(null);
  const [partDocumentDeletionReason, setPartDocumentDeletionReason] = useState("");
  const [partDocumentDeleting, setPartDocumentDeleting] = useState(false);
  const [partDocumentDeleteError, setPartDocumentDeleteError] = useState("");
  const partDocumentFileInputRef = useRef(null);
  const partDocumentDeletionReasonRef = useRef(null);
  const associatedMachinesRequestRef = useRef(0);
  const partDocumentsRequestRef = useRef(0);
  const token = store.token;
  const role = String(store.role || localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();
  const isAdmin = role === "admin";
  const canViewPartDocuments = role === "admin" || role === "tecnico";

  useEffect(() => {
    setPurchaseHistoryPage(1);
  }, [materialId]);

  useEffect(() => {
    const controller = new AbortController();

    const loadDetail = async () => {
      if (!token) {
        setDetail(null);
        return;
      }

      const params = new URLSearchParams({
        page: String(purchaseHistoryPage),
        per_page: String(PURCHASE_HISTORY_PAGE_SIZE),
      });
      if (sortConfig.key) {
        params.set("sort_by", sortConfig.key);
        params.set("sort_order", sortConfig.direction);
      }

      setDetailLoading(true);
      setDetailError("");

      try {
        const response = await fetch(
          `${API_URL}/api/catalog/parts/${encodeURIComponent(materialId)}?${params.toString()}`,
          {
            headers: { Authorization: "Bearer " + token },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => null);

        if (response.status === 401) {
          await actions.handleApiError(response);
          return;
        }

        if (!response.ok || !payload?.success) {
          setDetail(null);
          setDetailError(payload?.error || "No se pudo cargar el repuesto");
          return;
        }

        setDetail(payload.data || null);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Part detail load error:", error);
        setDetail(null);
        setDetailError("No se pudo cargar el repuesto");
      } finally {
        if (!controller.signal.aborted) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();

    return () => {
      controller.abort();
    };
  }, [materialId, purchaseHistoryPage, sortConfig, token]);

  const summary = detail?.summary || null;
  const purchaseHistory = Array.isArray(detail?.purchase_history?.rows)
    ? detail.purchase_history.rows
    : [];
  const purchaseHistoryAll = Array.isArray(detail?.purchase_history_all)
    ? detail.purchase_history_all
    : purchaseHistory;
  const purchaseHistoryPagination = detail?.purchase_history?.pagination || {
    page: 1,
    per_page: PURCHASE_HISTORY_PAGE_SIZE,
    total: purchaseHistory.length,
    pages: 1,
  };
  const suppliers = Array.isArray(detail?.suppliers) ? detail.suppliers : [];
  const inventoryCounts = detail?.inventory_counts || {
    inventory_count_in_total: 0,
    inventory_count_out_total: 0,
    movement_count: 0,
  };
  const orma = detail?.orma || {};
  const ormaMaterial = orma?.material || {};
  const ormaLocations = Array.isArray(orma?.locations)
    ? orma.locations
    : [];
  const ormaSummary = orma?.summary || {};
  const documentMaterialId = String(summary?.material_id || "").trim();

  const machineMaterialRecordId = useMemo(
    () => getCanonicalMaterialRecordId(detail),
    [detail],
  );

  const loadAssociatedMachines = useCallback(
    async (materialRecordId = machineMaterialRecordId) => {
      const normalizedRecordId = Number(materialRecordId);

      if (!Number.isInteger(normalizedRecordId) || normalizedRecordId <= 0) {
        associatedMachinesRequestRef.current += 1;
        setAssociatedMachines([]);
        setAssociatedMachinesLoading(false);
        setAssociatedMachinesError("");
        return;
      }

      const requestId = associatedMachinesRequestRef.current + 1;
      associatedMachinesRequestRef.current = requestId;
      setAssociatedMachinesLoading(true);
      setAssociatedMachinesError("");

      const params =
        associatedMachineMode === ""
          ? {}
          : { is_active: associatedMachineMode };

      const result = await actions.getMaterialMachines(
        normalizedRecordId,
        params,
      );

      if (associatedMachinesRequestRef.current !== requestId) return;

      if (!result?.success) {
        setAssociatedMachines([]);
        setAssociatedMachinesError(
          result?.error || "No se pudieron cargar las máquinas asociadas",
        );
        setAssociatedMachinesLoading(false);
        return;
      }

      setAssociatedMachines(getPartMachineItems(result.data));
      setAssociatedMachinesLoading(false);
    },
    [actions, associatedMachineMode, machineMaterialRecordId],
  );

  const loadPartDocuments = useCallback(
    async (targetMaterialId) => {
      const normalizedMaterialId = String(targetMaterialId || "").trim();
      if (!normalizedMaterialId) {
        setPartDocuments([]);
        setPartDocumentsLoading(false);
        setPartDocumentsError("");
        return;
      }

      const requestId = partDocumentsRequestRef.current + 1;
      partDocumentsRequestRef.current = requestId;
      setPartDocumentsLoading(true);
      setPartDocumentsError("");

      const result = await actions.getPartDocuments(normalizedMaterialId);
      if (partDocumentsRequestRef.current !== requestId) return;

      if (!result.success) {
        setPartDocuments([]);
        setPartDocumentsError(
          getPartDocumentErrorMessage(
            result,
            "No se pudo cargar la documentación del repuesto",
          ),
        );
        setPartDocumentsLoading(false);
        return;
      }

      setPartDocuments(Array.isArray(result.data) ? result.data : []);
      setPartDocumentsLoading(false);
    },
    [actions],
  );

  useEffect(() => {
    if (!machineMaterialRecordId) {
      associatedMachinesRequestRef.current += 1;
      setAssociatedMachines([]);
      setAssociatedMachinesLoading(false);
      setAssociatedMachinesError("");
      return undefined;
    }

    loadAssociatedMachines(machineMaterialRecordId);

    return () => {
      associatedMachinesRequestRef.current += 1;
    };
  }, [loadAssociatedMachines, machineMaterialRecordId]);

  useEffect(() => {
    if (!canViewPartDocuments || !documentMaterialId) {
      partDocumentsRequestRef.current += 1;
      setPartDocuments([]);
      setPartDocumentsLoading(false);
      setPartDocumentsError("");
      return;
    }

    loadPartDocuments(documentMaterialId);
    return () => {
      partDocumentsRequestRef.current += 1;
    };
  }, [canViewPartDocuments, documentMaterialId, loadPartDocuments]);

  useEffect(
    () => () => {
      if (partDocumentPreview?.objectUrl) {
        window.URL.revokeObjectURL(partDocumentPreview.objectUrl);
      }
    },
    [partDocumentPreview?.objectUrl],
  );

  useEffect(() => {
    if (partDocumentDeleteTarget) {
      partDocumentDeletionReasonRef.current?.focus();
    }
  }, [partDocumentDeleteTarget]);

  const analytics = useMemo(() => {
    const totalSpend = Number(summary?.total_spend || 0);
    const totalQuantity = Number(summary?.total_quantity || 0);
    const purchaseLines = Number(summary?.purchase_count || purchaseHistoryAll.length || 0);
    const supplierCount = Number(summary?.supplier_count || suppliers.length || 0);
    const sortedDates = purchaseHistoryAll
      .map((row) => row.date)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    const monthlySpendMap = new Map();

    purchaseHistoryAll.forEach((row) => {
      const monthKey = getMonthKey(row.date);
      if (!monthKey) return;

      const currentMonth = monthlySpendMap.get(monthKey) || {
        monthKey,
        month: formatMonthLabel(monthKey),
        totalSpend: 0,
        purchaseLines: 0,
      };

      currentMonth.totalSpend += Number(row.total_price || 0);
      currentMonth.purchaseLines += 1;
      monthlySpendMap.set(monthKey, currentMonth);
    });

    const monthlySpend = Array.from(monthlySpendMap.values()).sort((a, b) =>
      a.monthKey.localeCompare(b.monthKey),
    );
    const topSuppliersBySpend = suppliers.slice(0, 8).map((row) => ({
      ...row,
      supplierName: row.supplier_name || "Sin proveedor",
      totalSpend: Number(row.total_spend || 0),
      purchaseCount: Number(row.purchase_count || 0),
    }));

    return {
      kpis: {
        totalSpend,
        totalQuantity,
        purchaseLines,
        supplierCount,
        firstPurchaseDate: sortedDates[0] || null,
        lastPurchaseDate: sortedDates[sortedDates.length - 1] || null,
      },
      monthlySpend,
      topSuppliersBySpend,
    };
  }, [purchaseHistoryAll, summary, suppliers]);

  const hasSupplierDescription = purchaseHistory.some((row) =>
    Boolean(row.supplier_description),
  );
  const totalPurchaseHistoryPages = Math.max(
    1,
    Number(purchaseHistoryPagination.pages || 1),
  );
  const currentPurchaseHistoryPage = Math.min(
    Number(purchaseHistoryPagination.page || purchaseHistoryPage),
    totalPurchaseHistoryPages,
  );
  const paginatedPurchaseHistory = purchaseHistory;

  const handleSort = async (key) => {
    const nextSortConfig = {
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    };
    setSortConfig(nextSortConfig);
    setPurchaseHistoryPage(1);
  };

  const resetPartDocumentUpload = () => {
    setPartDocumentFile(null);
    setPartDocumentType("MANUAL");
    setPartDocumentDescription("");
    setPartDocumentUploadError("");
    if (partDocumentFileInputRef.current) {
      partDocumentFileInputRef.current.value = "";
    }
  };

  const handlePartDocumentUpload = async (event) => {
    event.preventDefault();
    if (!isAdmin || partDocumentSubmitting) return;

    if (!partDocumentFile) {
      setPartDocumentUploadError("Selecciona un archivo");
      return;
    }
    if (!partDocumentType) {
      setPartDocumentUploadError("Selecciona un tipo documental");
      return;
    }
    if (partDocumentFile.size > PART_DOCUMENT_MAX_SIZE_BYTES) {
      setPartDocumentUploadError("El archivo supera el límite de 10 MiB");
      return;
    }

    setPartDocumentSubmitting(true);
    setPartDocumentUploadError("");
    const result = await actions.uploadPartDocument(
      documentMaterialId,
      partDocumentFile,
      partDocumentType,
      partDocumentDescription,
    );
    setPartDocumentSubmitting(false);

    if (!result.success) {
      setPartDocumentUploadError(
        getPartDocumentErrorMessage(result, "No se pudo subir el documento"),
      );
      return;
    }

    resetPartDocumentUpload();
    setPartDocumentUploadOpen(false);
    actions.showToast("Documento subido correctamente", "success");
    await loadPartDocuments(documentMaterialId);
  };

  const handlePartDocumentPreview = async (documentItem) => {
    if (!documentItem?.can_preview || partDocumentPreviewingId) return;

    setPartDocumentPreview(null);
    setPartDocumentPreviewError("");
    setPartDocumentPreviewingId(documentItem.id);
    const result = await actions.previewPartDocument(
      documentMaterialId,
      documentItem.id,
    );
    setPartDocumentPreviewingId(null);

    if (!result.success || !result.data?.blob) {
      setPartDocumentPreviewError(
        getPartDocumentErrorMessage(
          result,
          "No se pudo cargar la vista previa",
        ),
      );
      return;
    }

    const objectUrl = window.URL.createObjectURL(result.data.blob);
    setPartDocumentPreview({
      objectUrl,
      contentType: result.data.contentType || documentItem.mime_type || "",
      filename: result.data.filename || documentItem.original_filename,
      document: documentItem,
    });
  };

  const handlePartDocumentDownload = async (documentItem) => {
    if (partDocumentDownloadingId) return;

    setPartDocumentDownloadingId(documentItem.id);
    setPartDocumentDownloadError("");
    const result = await actions.downloadPartDocument(
      documentMaterialId,
      documentItem.id,
      documentItem.original_filename,
    );
    setPartDocumentDownloadingId(null);

    if (!result.success) {
      setPartDocumentDownloadError(
        getPartDocumentErrorMessage(
          result,
          "No se pudo descargar el documento",
        ),
      );
    }
  };

  const openPartDocumentDeletion = (documentItem) => {
    setPartDocumentDeleteTarget(documentItem);
    setPartDocumentDeletionReason("");
    setPartDocumentDeleteError("");
  };

  const closePartDocumentDeletion = () => {
    if (partDocumentDeleting) return;
    setPartDocumentDeleteTarget(null);
    setPartDocumentDeletionReason("");
    setPartDocumentDeleteError("");
  };

  const handlePartDocumentDelete = async (event) => {
    event.preventDefault();
    const reason = partDocumentDeletionReason.trim();
    if (!isAdmin || !partDocumentDeleteTarget || partDocumentDeleting) return;
    if (!reason) {
      setPartDocumentDeleteError("Indica el motivo de eliminación");
      return;
    }

    setPartDocumentDeleting(true);
    setPartDocumentDeleteError("");
    const result = await actions.deletePartDocument(
      documentMaterialId,
      partDocumentDeleteTarget.id,
      reason,
    );
    setPartDocumentDeleting(false);

    if (!result.success) {
      setPartDocumentDeleteError(
        getPartDocumentErrorMessage(
          result,
          "No se pudo eliminar el documento",
        ),
      );
      return;
    }

    setPartDocumentDeleteTarget(null);
    setPartDocumentDeletionReason("");
    actions.showToast("Documento eliminado lógicamente", "success");
    await loadPartDocuments(documentMaterialId);
  };

  const renderSortableHeader = (key, label, className = "") => (
    <th className={className}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none text-reset fw-semibold"
        onClick={() => handleSort(key)}
      >
        {label}
        {getSortIndicator(sortConfig, key)}
      </button>
    </th>
  );

  return (
    <div className="container mt-3 catalog-page part-detail-page supplier-detail-page">
      <div className="page-header catalog-header supplier-detail-header">
        <div className="supplier-detail-heading">
          <div className="supplier-detail-title-row">
            <button
              type="button"
              className="btn btn-secondary supplier-detail-back-button"
              onClick={() => navigate("/parts-catalog")}
            >
              ← Volver
            </button>
            <span className="supplier-detail-chip part-detail-chip">Repuesto</span>
            <h1 className="page-title supplier-detail-title">
              {summary?.description || "Sin descripción"}
            </h1>
            <span className="supplier-detail-id">
              ID {summary?.material_id || materialId || "-"}
            </span>
          </div>
          {summary && (
            <p className="page-subtitle supplier-detail-subtitle">
              Ficha ORMA/VISOR del repuesto con stock, ubicaciones e historial operativo opcional.
            </p>
          )}
        </div>
      </div>

      {detailLoading && (
        <div className="catalog-state catalog-state-loading">
          Cargando detalle del repuesto...
        </div>
      )}

      {!detailLoading && detailError && (
        <div className="catalog-state catalog-state-error">
          {detailError}
        </div>
      )}

      {!detailLoading && !detailError && !summary && (
        <div className="catalog-state catalog-state-empty">
          No hay datos disponibles para este repuesto.
        </div>
      )}

      {!detailLoading && !detailError && summary && (
        <>
          <section className="catalog-card part-section-card">
            <div className="catalog-section-head">
              <div>
                <p className="catalog-eyebrow">Fuente principal: ORMA / VISOR</p>
                <h2>Datos del catálogo y disponibilidad</h2>
                <p>
                  La existencia, descripción, cantidad y ubicaciones proceden
                  del catálogo ORMA. Los movimientos históricos son opcionales.
                </p>
              </div>
            </div>

            <div className="part-summary-grid part-summary-grid-compact">
              <article className="part-summary-item">
                <span className="part-summary-label">Stock ORMA</span>
                <strong>{formatNumber(ormaSummary.total_quantity)}</strong>
              </article>
              <article className="part-summary-item">
                <span className="part-summary-label">Ubicaciones</span>
                <strong>{formatInteger(ormaSummary.location_count)}</strong>
              </article>
              <article className="part-summary-item">
                <span className="part-summary-label">Marca</span>
                <strong>{ormaMaterial.brand || "Sin dato"}</strong>
              </article>
              <article className="part-summary-item">
                <span className="part-summary-label">Modelo</span>
                <strong>{ormaMaterial.model || "Sin dato"}</strong>
              </article>
              <article className="part-summary-item">
                <span className="part-summary-label">Alias</span>
                <strong>{ormaMaterial.alias || "Sin dato"}</strong>
              </article>
              <article className="part-summary-item">
                <span className="part-summary-label">Ref. fabricante</span>
                <strong>{ormaMaterial.manufacturer_ref || "Sin dato"}</strong>
              </article>
            </div>

            <div className="catalog-state catalog-state-inline">
              <strong>
                {ormaMaterial.master_description ||
                  ormaMaterial.visor_description ||
                  summary?.description}
              </strong>
              {ormaMaterial.supplier_ref && (
                <p>Referencia suministrador: {ormaMaterial.supplier_ref}</p>
              )}
            </div>

            {ormaLocations.length === 0 ? (
              <div className="catalog-state catalog-state-inline">
                El repuesto existe en ORMA, pero no tiene ubicación informada.
              </div>
            ) : (
              <div className="table-responsive catalog-table-scroll">
                <table className="table table-compact mb-0">
                  <thead>
                    <tr>
                      <th>Sucursal</th>
                      <th>Almacén</th>
                      <th>Columna</th>
                      <th>Estante</th>
                      <th>Gabeta</th>
                      <th>2ª ubicación</th>
                      <th className="text-end">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ormaLocations.map((location) => (
                      <tr key={location.id}>
                        <td>{location.branch_name || "-"}</td>
                        <td>{location.warehouse_id || "-"}</td>
                        <td>{location.column_location || "-"}</td>
                        <td>{location.shelf || "-"}</td>
                        <td>{location.bin || "-"}</td>
                        <td>{location.second_location || "-"}</td>
                        <td className="text-end">
                          {formatNumber(location.quantity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="supplier-analytics-kpis part-analytics-kpis">
            <article className="supplier-analytics-kpi supplier-analytics-kpi-spend">
              <span className="supplier-analytics-kpi-label">Gasto total</span>
              <strong>{formatCurrency(analytics.kpis.totalSpend)}</strong>
            </article>
            <article className="supplier-analytics-kpi supplier-analytics-kpi-lines">
              <span className="supplier-analytics-kpi-label">Cantidad comprada</span>
              <strong>{formatNumber(analytics.kpis.totalQuantity)}</strong>
            </article>
            <article className="supplier-analytics-kpi supplier-analytics-kpi-parts">
              <span className="supplier-analytics-kpi-label">Compras / líneas</span>
              <strong>{formatInteger(analytics.kpis.purchaseLines)}</strong>
            </article>
            <article className="supplier-analytics-kpi supplier-analytics-kpi-average">
              <span className="supplier-analytics-kpi-label">Proveedores</span>
              <strong>{formatInteger(analytics.kpis.supplierCount)}</strong>
            </article>
            <article className="supplier-analytics-kpi supplier-analytics-kpi-first">
              <span className="supplier-analytics-kpi-label">Primera compra</span>
              <strong>{formatDate(analytics.kpis.firstPurchaseDate)}</strong>
            </article>
            <article className="supplier-analytics-kpi supplier-analytics-kpi-last">
              <span className="supplier-analytics-kpi-label">Última compra</span>
              <strong>{formatDate(analytics.kpis.lastPurchaseDate)}</strong>
            </article>
          </section>

          <section className="supplier-analytics-charts">
            <article className="catalog-card supplier-analytics-chart">
              <div className="catalog-section-head">
                <div>
                  <h2>Compras por mes</h2>
                  <p>Evolución mensual del importe comprado.</p>
                </div>
              </div>

              {analytics.monthlySpend.length === 0 ? (
                <div className="catalog-state catalog-state-inline">
                  No hay fechas suficientes para calcular compras por mes.
                </div>
              ) : (
                <div className="supplier-analytics-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analytics.monthlySpend}
                      margin={{ top: 6, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid stroke="#e5e7eb" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} tickMargin={6} />
                      <YAxis
                        width={76}
                        tick={{ fontSize: 11 }}
                        tickMargin={6}
                        tickFormatter={(value) =>
                          currencyFormatter.format(Number(value || 0))
                        }
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), "Gasto"]}
                        labelFormatter={(label) => `Mes: ${label}`}
                      />
                      <Bar dataKey="totalSpend" name="Gasto" fill="#0f766e" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </article>

            <article className="catalog-card supplier-analytics-chart">
              <div className="catalog-section-head">
                <div>
                  <h2>Top proveedores por gasto</h2>
                  <p>Proveedores asociados a este repuesto con mayor importe comprado.</p>
                </div>
              </div>

              {analytics.topSuppliersBySpend.length === 0 ? (
                <div className="catalog-state catalog-state-inline">
                  No hay proveedores suficientes para representar el top de gasto.
                </div>
              ) : (
                <div className="supplier-analytics-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analytics.topSuppliersBySpend}
                      layout="vertical"
                      margin={{ top: 6, right: 8, left: 12, bottom: 0 }}
                    >
                      <CartesianGrid stroke="#e5e7eb" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) =>
                          currencyFormatter.format(Number(value || 0))
                        }
                      />
                      <YAxis
                        dataKey="supplierName"
                        type="category"
                        width={118}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => truncateText(value, 18)}
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), "Gasto"]}
                        labelFormatter={(label) => `Proveedor: ${label}`}
                      />
                      <Bar dataKey="totalSpend" name="Gasto" fill="#2563eb" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </article>
          </section>

          <section
        className="catalog-card part-section-card part-machines-card"
        aria-busy={associatedMachinesLoading}
      >
        <div className="catalog-section-head part-machines-head">
          <div>
            <p className="catalog-eyebrow">Aplicación técnica</p>
            <h2>Máquinas asociadas</h2>
            <p>Relaciones registradas entre este material y las máquinas de fábrica.</p>
          </div>

          <label className="part-machines-filter">
            Relaciones
            <select
              className="form-select"
              value={associatedMachineMode}
              onChange={(event) => setAssociatedMachineMode(event.target.value)}
            >
              <option value="true">Activas</option>
              <option value="false">Inactivas</option>
              <option value="">Todas</option>
            </select>
          </label>
        </div>

        {!machineMaterialRecordId && (
          <div className="catalog-state catalog-state-inline">
            <p>
              La respuesta del repuesto no contiene su ID canónico interno.
              La consulta no se realizará usando el código visible de la URL.
            </p>
          </div>
        )}

        {machineMaterialRecordId && associatedMachinesLoading && (
          <div className="catalog-state catalog-state-inline">
            <div className="spinner-border text-primary" role="status" />
            <p>Cargando máquinas asociadas?</p>
          </div>
        )}

        {machineMaterialRecordId && !associatedMachinesLoading && associatedMachinesError && (
          <div className="catalog-state catalog-state-inline catalog-state-error">
            <p>{associatedMachinesError}</p>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => loadAssociatedMachines(machineMaterialRecordId)}
            >
              Reintentar
            </button>
          </div>
        )}

        {machineMaterialRecordId && !associatedMachinesLoading && !associatedMachinesError && associatedMachines.length === 0 && (
          <div className="catalog-state catalog-state-empty catalog-state-inline">
            <p>No hay relaciones de máquina para el filtro seleccionado.</p>
          </div>
        )}

        {machineMaterialRecordId && !associatedMachinesLoading && !associatedMachinesError && associatedMachines.length > 0 && (
          <div className="table-responsive catalog-table-scroll part-machines-table-wrap">
            <table className="table table-compact mb-0 part-machines-table">
              <caption className="visually-hidden">Máquinas asociadas al repuesto</caption>
              <thead>
                <tr>
                  <th>Máquina</th>
                  <th>Línea / sector</th>
                  <th>Ubicación</th>
                  <th>Estado</th>
                  <th>Tipo</th>
                  <th>Cantidad</th>
                  <th>Posición</th>
                  <th>Criticidad</th>
                  <th>Relación</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {associatedMachines.map((relation) => {
                  const machine = getMachineRelationMachine(relation);
                  const unit = getMachineRelationUnit(relation, machine);
                  const relatedMachineId = relation?.machine_id ?? machine?.id;

                  return (
                    <tr key={relation?.id ?? `${relatedMachineId}-${relation?.material_id}`}>
                      <td>
                        <strong>{machine?.machine_code || "Sin código"}</strong>
                        <small>{machine?.machine_name || "Sin nombre"}</small>
                      </td>
                      <td>
                        {unit?.unit_code || unit?.unit_name ? (
                          <>
                            <span>{unit?.unit_code || "Sin código"}</span>
                            <small>{unit?.unit_name || "Sin nombre"}</small>
                          </>
                        ) : "Sin unidad"}
                      </td>
                      <td>{machine?.location_description || relation?.location_description || "Sin dato"}</td>
                      <td>{machine?.status || "Sin dato"}</td>
                      <td>{getMachineRelationLabel(PART_MACHINE_RELATIONSHIP_TYPES, relation?.relationship_type)}</td>
                      <td>{formatNumber(relation?.quantity_required)}</td>
                      <td>{relation?.position_reference || "Sin dato"}</td>
                      <td>{getMachineRelationLabel(PART_MACHINE_CRITICALITIES, relation?.criticality)}</td>
                      <td>
                        <span className={`part-machine-status ${relation?.is_active === false ? "part-machine-status-inactive" : "part-machine-status-active"}`}>
                          {relation?.is_active === false ? "Inactiva" : "Activa"}
                        </span>
                      </td>
                      <td>
                        {relatedMachineId ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/machines/${relatedMachineId}`)}
                          >
                            Ver máquina
                          </button>
                        ) : "Sin enlace"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canViewPartDocuments && (
            <section
              className="catalog-card part-section-card part-documents-card"
              aria-busy={partDocumentsLoading}
            >
              <div className="catalog-section-head part-documents-head">
                <div>
                  <h2>Documentación</h2>
                  <p>Documentos técnicos y administrativos asociados al repuesto.</p>
                </div>
                {isAdmin && !partDocumentUploadOpen && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setPartDocumentUploadOpen(true);
                      setPartDocumentUploadError("");
                    }}
                  >
                    Añadir documento
                  </button>
                )}
              </div>

              {isAdmin && partDocumentUploadOpen && (
                <form
                  className="part-documents-upload-form"
                  onSubmit={handlePartDocumentUpload}
                  aria-busy={partDocumentSubmitting}
                >
                  <div className="part-documents-upload-fields">
                    <div className="form-group">
                      <label htmlFor="part_document_file">Archivo</label>
                      <input
                        ref={partDocumentFileInputRef}
                        id="part_document_file"
                        className="form-control"
                        type="file"
                        accept={PART_DOCUMENT_ACCEPT}
                        disabled={partDocumentSubmitting}
                        onChange={(event) => {
                          setPartDocumentFile(event.target.files?.[0] || null);
                          setPartDocumentUploadError("");
                        }}
                      />
                      <small className="part-documents-file-meta">
                        {partDocumentFile
                          ? `Seleccionado: ${partDocumentFile.name}`
                          : "PDF, Office o imagen. Máximo 10 MiB."}
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="part_document_type">Tipo documental</label>
                      <select
                        id="part_document_type"
                        className="form-select"
                        value={partDocumentType}
                        disabled={partDocumentSubmitting}
                        onChange={(event) => setPartDocumentType(event.target.value)}
                      >
                        {PART_DOCUMENT_TYPES.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group part-documents-description-field">
                      <label htmlFor="part_document_description">
                        Descripción opcional
                      </label>
                      <input
                        id="part_document_description"
                        className="form-control"
                        value={partDocumentDescription}
                        maxLength={500}
                        disabled={partDocumentSubmitting}
                        onChange={(event) =>
                          setPartDocumentDescription(event.target.value)
                        }
                      />
                    </div>
                  </div>
                  {partDocumentUploadError && (
                    <div className="catalog-state catalog-state-error catalog-state-inline">
                      {partDocumentUploadError}
                    </div>
                  )}
                  <div className="part-documents-form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={partDocumentSubmitting}
                      onClick={() => {
                        resetPartDocumentUpload();
                        setPartDocumentUploadOpen(false);
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={partDocumentSubmitting || !partDocumentFile}
                    >
                      {partDocumentSubmitting ? "Subiendo..." : "Subir documento"}
                    </button>
                  </div>
                </form>
              )}

              {partDocumentsLoading && (
                <div className="catalog-state catalog-state-loading catalog-state-inline">
                  Cargando documentación...
                </div>
              )}
              {!partDocumentsLoading && partDocumentsError && (
                <div className="catalog-state catalog-state-error catalog-state-inline">
                  {partDocumentsError}
                </div>
              )}
              {partDocumentPreviewError && (
                <div className="catalog-state catalog-state-error catalog-state-inline">
                  {partDocumentPreviewError}
                </div>
              )}
              {partDocumentDownloadError && (
                <div className="catalog-state catalog-state-error catalog-state-inline">
                  {partDocumentDownloadError}
                </div>
              )}

              {!partDocumentsLoading &&
                !partDocumentsError &&
                partDocuments.length === 0 && (
                  <div className="catalog-state catalog-state-empty catalog-state-inline part-documents-empty">
                    <span>No hay documentos asociados a este repuesto.</span>
                    {isAdmin && !partDocumentUploadOpen && (
                      <button
                        type="button"
                        className="catalog-detail-button"
                        onClick={() => setPartDocumentUploadOpen(true)}
                      >
                        Añadir el primero
                      </button>
                    )}
                  </div>
                )}

              {!partDocumentsLoading &&
                !partDocumentsError &&
                partDocuments.length > 0 && (
                  <div className="table-responsive catalog-table-scroll part-documents-table-wrap">
                    <table className="table table-compact mb-0 part-documents-table">
                      <thead>
                        <tr>
                          <th>Documento</th>
                          <th>Tipo</th>
                          <th>Descripción</th>
                          <th className="text-end">Tamaño</th>
                          <th>Fecha</th>
                          <th>Usuario</th>
                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partDocuments.map((documentItem) => (
                          <tr key={documentItem.id}>
                            <td className="part-document-name">
                              {documentItem.original_filename || "-"}
                            </td>
                            <td>
                              {PART_DOCUMENT_TYPE_LABELS[documentItem.document_type] ||
                                documentItem.document_type ||
                                "-"}
                            </td>
                            <td className="part-document-description">
                              {documentItem.description || "-"}
                            </td>
                            <td className="text-end">
                              {formatFileSize(documentItem.file_size)}
                            </td>
                            <td>{formatDateTime(documentItem.uploaded_at)}</td>
                            <td>{documentItem.uploaded_by?.username || "-"}</td>
                            <td className="text-end">
                              <div className="part-document-actions">
                                {documentItem.can_preview && (
                                  <button
                                    type="button"
                                    className="catalog-detail-button"
                                    disabled={
                                      partDocumentPreviewingId === documentItem.id
                                    }
                                    onClick={() =>
                                      handlePartDocumentPreview(documentItem)
                                    }
                                  >
                                    {partDocumentPreviewingId === documentItem.id
                                      ? "Cargando..."
                                      : "Vista previa"}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="catalog-detail-button"
                                  disabled={
                                    partDocumentDownloadingId === documentItem.id
                                  }
                                  onClick={() =>
                                    handlePartDocumentDownload(documentItem)
                                  }
                                >
                                  {partDocumentDownloadingId === documentItem.id
                                    ? "Descargando..."
                                    : "Descargar"}
                                </button>
                                {isAdmin && (
                                  <button
                                    type="button"
                                    className="catalog-detail-button part-document-delete-button"
                                    disabled={partDocumentDeleting}
                                    onClick={() =>
                                      openPartDocumentDeletion(documentItem)
                                    }
                                  >
                                    Eliminar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </section>
          )}

          {canViewPartDocuments && partDocumentPreview && (
            <div
              className="supplier-document-modal-backdrop part-document-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setPartDocumentPreview(null);
                }
              }}
            >
              <section
                className="supplier-document-modal part-document-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="part-document-preview-title"
              >
                <div className="supplier-document-modal-header">
                  <div>
                    <h2 id="part-document-preview-title">Vista previa</h2>
                    <p>{partDocumentPreview.filename || "Documento"}</p>
                  </div>
                  <button
                    type="button"
                    className="catalog-detail-button"
                    onClick={() => setPartDocumentPreview(null)}
                  >
                    Cerrar
                  </button>
                </div>
                <div className="supplier-document-preview-body part-document-preview-body">
                  {partDocumentPreview.contentType.includes("application/pdf") ? (
                    <iframe
                      className="supplier-document-preview-frame part-document-preview-frame"
                      src={partDocumentPreview.objectUrl}
                      title={partDocumentPreview.filename || "Vista previa PDF"}
                    />
                  ) : partDocumentPreview.contentType.startsWith("image/") ? (
                    <img
                      className="supplier-document-preview-image part-document-preview-image"
                      src={partDocumentPreview.objectUrl}
                      alt={partDocumentPreview.filename || "Vista previa del documento"}
                    />
                  ) : (
                    <div className="catalog-state catalog-state-empty catalog-state-inline">
                      Este formato no admite vista previa integrada.
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}

          {isAdmin && partDocumentDeleteTarget && (
            <div
              className="supplier-document-modal-backdrop part-document-delete-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closePartDocumentDeletion();
                }
              }}
            >
              <form
                className="supplier-document-modal part-document-delete-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="part-document-delete-title"
                aria-busy={partDocumentDeleting}
                onSubmit={handlePartDocumentDelete}
              >
                <div className="supplier-document-modal-header">
                  <div>
                    <h2 id="part-document-delete-title">Eliminar documento</h2>
                    <p>{partDocumentDeleteTarget.original_filename}</p>
                  </div>
                </div>
                <div className="supplier-document-preview-body part-document-delete-body">
                  <p className="part-document-delete-warning">
                    El documento dejará de estar disponible, pero el archivo físico
                    se conservará para trazabilidad.
                  </p>
                  <div className="form-group part-document-delete-reason">
                    <label htmlFor="part_document_deletion_reason">
                      Motivo de eliminación
                    </label>
                    <textarea
                      ref={partDocumentDeletionReasonRef}
                      id="part_document_deletion_reason"
                      className="form-control"
                      value={partDocumentDeletionReason}
                      maxLength={2000}
                      required
                      disabled={partDocumentDeleting}
                      onChange={(event) => {
                        setPartDocumentDeletionReason(event.target.value);
                        setPartDocumentDeleteError("");
                      }}
                    />
                  </div>
                  {partDocumentDeleteError && (
                    <div className="catalog-state catalog-state-error catalog-state-inline">
                      {partDocumentDeleteError}
                    </div>
                  )}
                </div>
                <div className="supplier-document-modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={partDocumentDeleting}
                    onClick={closePartDocumentDeletion}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-danger"
                    disabled={
                      partDocumentDeleting ||
                      !partDocumentDeletionReason.trim()
                    }
                  >
                    {partDocumentDeleting ? "Eliminando..." : "Eliminar"}
                  </button>
                </div>
              </form>
            </div>
          )}

          <section className="catalog-card part-section-card">
            <div className="catalog-section-head">
              <div>
                <h2>Histórico de compras de este repuesto</h2>
                <p>
                  Detalle de las líneas de compra registradas para este material. La
                  ordenación por columna se aplica en backend antes de paginar.
                </p>
              </div>
            </div>

            {purchaseHistory.length === 0 ? (
              <div className="catalog-state catalog-state-inline">
                No hay compras registradas para este repuesto.
              </div>
            ) : (
              <div>
                <div className="table-responsive catalog-table-scroll">
                  <table className="table table-compact mb-0 supplier-purchase-history-table part-purchase-history-table">
                    <thead>
                      <tr>
                        {renderSortableHeader("date", "Fecha")}
                        {renderSortableHeader("supplier", "Proveedor")}
                        {hasSupplierDescription && <th>Descripción proveedor</th>}
                        {renderSortableHeader("quantity", "Cantidad", "text-end")}
                        {renderSortableHeader("unit_price", "Precio unitario", "text-end")}
                        {renderSortableHeader("total_price", "Precio total", "text-end")}
                        <th>Batch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPurchaseHistory.map((row, index) => (
                        <tr key={`${row.date || "no-date"}-${row.supplier_name || "no-supplier"}-${row.batch_id || "no-batch"}-${(currentPurchaseHistoryPage - 1) * PURCHASE_HISTORY_PAGE_SIZE + index}`}>
                          <td className="supplier-purchase-history-date">{row.date || "-"}</td>
                          <td className="part-purchase-history-supplier">
                            {row.supplier_id ? (
                              <Link
                                className="catalog-link-button supplier-part-link"
                                to={getSupplierDetailPath(row.supplier_id)}
                                title={row.supplier_name || row.supplier_id}
                              >
                                {row.supplier_name || row.supplier_id}
                              </Link>
                            ) : (
                              row.supplier_name || "-"
                            )}
                          </td>
                          {hasSupplierDescription && (
                            <td
                              className="supplier-purchase-history-description"
                              title={row.supplier_description || ""}
                            >
                              {truncateText(row.supplier_description)}
                            </td>
                          )}
                          <td className="text-end supplier-purchase-history-number">
                            {formatNumber(row.quantity)}
                          </td>
                          <td className="text-end supplier-purchase-history-money">
                            {formatCurrency(row.unit_price)}
                          </td>
                          <td className="text-end supplier-purchase-history-money">
                            {formatCurrency(row.total_price)}
                          </td>
                          <td className="supplier-purchase-history-batch">{row.batch_id || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="catalog-pagination">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={currentPurchaseHistoryPage === 1}
                    onClick={() =>
                      setPurchaseHistoryPage((currentPage) =>
                        Math.max(1, currentPage - 1),
                      )
                    }
                  >
                    Anterior
                  </button>
                  <span>
                    Página {currentPurchaseHistoryPage} de {totalPurchaseHistoryPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={currentPurchaseHistoryPage === totalPurchaseHistoryPages}
                    onClick={() =>
                      setPurchaseHistoryPage((currentPage) =>
                        Math.min(totalPurchaseHistoryPages, currentPage + 1),
                      )
                    }
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </section>

          <section className="catalog-card part-section-card">
            <div className="catalog-section-head">
              <div>
                <h2>Movimientos de inventario</h2>
                <p>Entradas y salidas de inventario registradas para este repuesto.</p>
              </div>
            </div>

            <div className="part-summary-grid part-summary-grid-compact">
              <article className="part-summary-item">
                <span className="part-summary-label">Entradas</span>
                <strong>{formatNumber(inventoryCounts.inventory_count_in_total)}</strong>
              </article>
              <article className="part-summary-item">
                <span className="part-summary-label">Salidas</span>
                <strong>{formatNumber(inventoryCounts.inventory_count_out_total)}</strong>
              </article>
              <article className="part-summary-item">
                <span className="part-summary-label">Movimientos</span>
                <strong>{formatInteger(inventoryCounts.movement_count)}</strong>
              </article>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default PartDetail;
